/**
 * Registratie-bewaker: herkent een "zombie"-registratie aan gesprekssignalen.
 *
 * Zombie = de Telnyx-SDK zegt 'registered', maar Telnyx kan de browser niet
 * (meer) bereiken. De SDK sluit een halfdode websocket nooit: blijft een pong
 * uit, dan stuurt hij alleen een nieuwe ping en wacht door. Onze health-check
 * (checkConnectionHealth) vertrouwt op die SDK-status en grijpt alleen in bij
 * token-verval, slaap of netwerkwissel. Tot nu toe heelde alleen de
 * 45-minuten-tokenverversing of een herstart van de pc dit.
 *
 * Meting 22-10-2026 (Ilse, 60 dagen): 28 van 809 uitgaande gesprekken
 * strandden na het opnemen van de A-leg (Telnyx zag het antwoord nooit, gaf
 * na 45 s 'timeout'), 11 wees Telnyx binnen 1,5 s af omdat er geen registratie
 * was ('unspecified'), en op 21-10 07:56 kwamen vier inkomende gesprekken wel
 * via Realtime binnen maar nooit als SDK-invite (answer_failed/no_sdk_call).
 *
 * Drie signalen, elk met een eigen drempel. De beslislogica is puur zodat ze
 * getest kan worden; client.ts voert de herverbinding uit en bewaakt het
 * effect (max. één geforceerde herverbinding per minuut, nooit tijdens een
 * lopend gesprek).
 */

import type { CallState, EventSource } from './types';

export type SuspectReason =
  | 'a_leg_unreachable'
  | 'answer_not_received'
  | 'invite_not_delivered';

/** Uitgaand: Telnyx meldt de A-leg binnen deze tijd 'completed' zonder dat de browser hem zag. */
export const A_LEG_UNREACHABLE_MAX_MS = 6_000;
/** Uitgaand: de SDK laat de opgenomen A-leg binnen deze tijd vallen zonder dat media ging lopen. */
export const ANSWER_NOT_RECEIVED_MAX_MS = 12_000;
/**
 * Uitgaand: na zo'n snelle SDK-val wachten we dit lang op de server-terminal
 * (Realtime 'completed'). Komt die mee, dan was het een gewone snelle
 * afwijzing (bezet, geweigerd) en geen zombie. Blijft hij uit, dan leeft de
 * A-leg bij Telnyx nog (die hangt pas na 45 s op met 'timeout').
 */
export const SERVER_TERMINAL_GRACE_MS = 4_000;
/** Inkomend: een Realtime-seed ('ring'-stage) hoort binnen deze tijd een SDK-invite te krijgen. */
export const INVITE_WAIT_MS = 5_000;
/** Niet verdenken vlak na een gesprek: de server-side busy-cache (60 s) dialt ons dan bewust niet. */
export const POST_CALL_QUIET_MS = 60_000;
/** Effectcontrole: hoogstens één geforceerde herverbinding per venster. */
export const FORCED_RECONNECT_MIN_GAP_MS = 60_000;

export interface OutboundEndInput {
  direction: 'inbound' | 'outbound';
  /** Record-state vlak vóór DESTROYED ('dialing' | 'ending' | ...). */
  stateBeforeEnd: CallState;
  answeredAt: Date | null;
  startedAt: Date;
  /** Wanneer de browser de A-leg auto-beantwoordde (bridge_leg_answered), of null. */
  bridgeAnsweredAt: number | null;
  endedAt: number;
  /** finalizeCall-reden: SDK 'hangup'/'destroy' of Realtime-status ('completed', 'failed', ...). */
  reason: string;
  source: EventSource;
}

export type OutboundVerdict =
  | { kind: 'none' }
  | { kind: 'suspect'; reason: 'a_leg_unreachable'; elapsedMs: number }
  /** Pas verdacht als de server-terminal binnen SERVER_TERMINAL_GRACE_MS uitblijft. */
  | { kind: 'await_server_terminal'; reason: 'answer_not_received'; elapsedMs: number };

export function assessOutboundEnd(i: OutboundEndInput): OutboundVerdict {
  if (i.direction !== 'outbound') return { kind: 'none' };
  if (i.answeredAt) return { kind: 'none' };
  // Gebruiker hing zelf op tijdens het kiezen: geen oordeel.
  if (i.stateBeforeEnd === 'ending' || i.stateBeforeEnd === 'ended') return { kind: 'none' };

  if (i.bridgeAnsweredAt === null) {
    // A-leg kwam nooit aan. 'failed' is een server-dial_failed (bv. 403 op een
    // ongeldig nummer) en zegt niets over de registratie; alleen een snelle
    // 'completed' van Telnyx (hangup 'unspecified' na ~1,5 s) telt.
    const elapsedMs = i.endedAt - i.startedAt.getTime();
    if (i.source === 'realtime' && i.reason === 'completed' && elapsedMs <= A_LEG_UNREACHABLE_MAX_MS) {
      return { kind: 'suspect', reason: 'a_leg_unreachable', elapsedMs };
    }
    return { kind: 'none' };
  }

  // Browser nam de A-leg op, maar de SDK liet hem snel weer vallen zonder dat
  // media ging lopen. Een bezette of weigerende klant ziet er lokaal hetzelfde
  // uit; het verschil is dat Telnyx dán óók meteen finaliseert (Realtime
  // 'completed' binnen een paar seconden). Daarom: eerst wachten.
  const elapsedMs = i.endedAt - i.bridgeAnsweredAt;
  if (i.source === 'sdk' && elapsedMs <= ANSWER_NOT_RECEIVED_MAX_MS) {
    return { kind: 'await_server_terminal', reason: 'answer_not_received', elapsedMs };
  }
  return { kind: 'none' };
}

export interface SuspectGateInput {
  now: number;
  hasActiveCall: boolean;
  reconnectBusy: boolean;
  lastForcedReconnectAt: number;
  /** Alleen relevant voor 'invite_not_delivered'. */
  lastCallEndedAt: number;
  reason: SuspectReason;
}

export type SuspectAction =
  | 'reconnect'
  | 'skipped_active_call'
  | 'skipped_reconnecting'
  | 'skipped_recent'
  | 'skipped_post_call_quiet';

/** Effectcontrole: wanneer mag een verdenking echt een herverbinding forceren? */
export function decideSuspectAction(i: SuspectGateInput): SuspectAction {
  if (i.hasActiveCall) return 'skipped_active_call';
  if (i.reconnectBusy) return 'skipped_reconnecting';
  if (i.reason === 'invite_not_delivered' && i.now - i.lastCallEndedAt < POST_CALL_QUIET_MS) {
    return 'skipped_post_call_quiet';
  }
  if (i.now - i.lastForcedReconnectAt < FORCED_RECONNECT_MIN_GAP_MS) return 'skipped_recent';
  return 'reconnect';
}

export function noticeFor(reason: SuspectReason): string {
  switch (reason) {
    case 'invite_not_delivered':
      return 'De telefoonverbinding was stil weggevallen en is zojuist hersteld. Deze beller is doorgeschakeld of naar de voicemail gegaan; bel terug via Gemist.';
    case 'a_leg_unreachable':
    case 'answer_not_received':
      return 'De telefoonverbinding was stil weggevallen en is zojuist hersteld. Bel opnieuw.';
  }
}
